import { ChevronRight, QrCode } from "lucide-react";

import type { PayinRuntimeEntity } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { ActivityStatusPill } from "@/modules/dashboard/payments-activity/components/ActivityStatusPill";
import { adaptPayinRow } from "@/modules/dashboard/payments-activity/utils/activityAdapters";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { currencyFractionDigits, resolveLocale } from "../utils/formatWalletAmount";

export interface WalletPendingCollectionsProps {
  collections: PayinRuntimeEntity[];
  onSelect: (payin: PayinRuntimeEntity) => void;
}

/**
 * The collections this wallet is still waiting on, above the statement rather than inside it.
 *
 * Deliberately not a statement row: nothing has moved yet, so these have no `balance_after` and no posting
 * date, and folding them into the day-grouped ledger would claim they are posted movements. Kept as its own
 * band, they read as what they are — the itemised version of the "pending" figure in the hero. Each opens the
 * same operation drawer the activity screen uses, which is where an unpaid QR can be shown again.
 */
export const WalletPendingCollections = ({ collections, onSelect }: WalletPendingCollectionsProps) => {
  const { t, activeLanguage } = useTranslations("wallets");

  if (collections.length === 0) return null;

  return (
    <ColocaSurface padding="none" className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-4 py-2.5">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("pending_collections_title")}
        </p>
        <span className="text-[11px] text-muted-foreground">
          {collections.length === 1
            ? t("pending_collections_count_one")
            : t("pending_collections_count_other", { count: collections.length })}
        </span>
      </div>

      {collections.map((payin) => {
        const row = adaptPayinRow(payin);
        const decimals = currencyFractionDigits(row.currency);
        const createdAt = new Date(payin.created_at);
        const createdLabel = Number.isNaN(createdAt.getTime())
          ? null
          : new Intl.DateTimeFormat(resolveLocale(activeLanguage), {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            }).format(createdAt);
        return (
          <button
            key={payin.id}
            type="button"
            onClick={() => onSelect(payin)}
            className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-3.5 border-b border-border bg-background px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/40 focus:bg-muted/40 focus:outline-none"
          >
            <div
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-primary/25 bg-primary/10 text-primary"
              aria-hidden="true"
            >
              <QrCode size={14} />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <ActivityStatusPill row={row} />
                <span className="truncate text-[13px] font-medium text-foreground">
                  {formatRail(row.rail)}
                </span>
              </div>
              <p className="mt-0.5 truncate text-[12px] text-muted-foreground">
                {t("pending_collections_awaiting")}
                {createdLabel ? ` · ${createdLabel}` : ""}
              </p>
            </div>

            <div className="flex items-center gap-1.5">
              <ColocaAmountDisplay
                value={row.amount}
                currency={row.currency}
                minimumFractionDigits={decimals.min}
                maximumFractionDigits={decimals.max}
                locale={resolveLocale(activeLanguage)}
                size="sm"
                className="font-medium text-foreground"
              />
              <ChevronRight size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
            </div>
          </button>
        );
      })}
    </ColocaSurface>
  );
};
